import { FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Page } from '~src/domains/common/enums/api';
import { AlertNames, FullSSNFormNames } from '~src/domains/common/enums/common';
import {
  customerNamesSelector,
  localeSelector,
  mcodeSelector,
} from '~src/domains/common/state/commonSelectors';
import { formatCustomerNames } from '~src/utils/helpers';
import { trackPageMount, trackFocus } from '~src/utils/tracking';
import { validate } from '~src/utils/validation';
import { xhrNext } from '~src/utils/xhr';
import * as styles from '~src/styles/domains/fullSSN/FullSSNPage.module.scss';
import Alerter from '../common/components/Alerter';
import SimpleButton from '../common/components/Button';
import InputGroup from '../common/components/InputGroup';
import { updateFormMeta } from '~src/utils/forms';
import * as fullSSNFormActions from '~src/domains/fullSSN/state/fullSSNFormReducer';
import { fullSSNFormSelector } from '~src/domains/fullSSN/state/fullSSNSelectors';
import { setMcode } from '../common/state/commonReducer';

const FullSSNPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const customerNames = useSelector(customerNamesSelector);
  const locale = useSelector(localeSelector);
  const mcode = useSelector(mcodeSelector);
  const fullSSNForm = useSelector(fullSSNFormSelector) as {
    [key in FullSSNFormNames]: any;
  };

  const { firstName } = formatCustomerNames(customerNames);

  const inputs = t('fullSSN:inputs', {
    returnObjects: true,
  }) as fullSSNFormActions.FullSSNLocalization;

  useEffect(() => {
    dispatch(trackPageMount(Page.FULL_SSN));
    dispatch(updateFormMeta(inputs, fullSSNFormActions));
  }, []);

  useEffect(() => {
    dispatch(updateFormMeta(inputs, fullSSNFormActions));
  }, [locale]);

  const inputGroup = (
    name: FullSSNFormNames,
    optionalConfig?: { extras?: string[] },
  ) => {
    return (
      <InputGroup
        extras={optionalConfig?.extras || []}
        page={Page.FULL_SSN}
        id={name}
        input={inputs[name]}
        state={fullSSNForm[name]}
        reduxActions={fullSSNFormActions}
      />
    );
  };

  const onClickContinue = () => {
    // Reset mcode before proceeding
    dispatch(setMcode(null));

    const errors = validate(fullSSNForm);

    errors.forEach((error, idx: number) => {
      if (idx === 0 && error.input) {
        // Focus on the first invalid input
        const errorElement = document.getElementById(error.name);
        errorElement?.focus();
      }
      // Update validity state for each error
      dispatch(
        fullSSNFormActions.updateInputIsValid({
          name: error.name,
          msg: error.errorMsg,
        }),
      );
    });

    if (errors.length === 0) {
      // No errors - proceed to next step
      dispatch(xhrNext());
    } else {
      // Track focus on the first invalid field
      dispatch(trackFocus(['fullSSNForm', errors[0].name, 'value']));
    }
  };

  return (
    <div className={styles['full-ssn-wrapper']}>
      {mcode && (
        <Alerter type={AlertNames.ERROR} i18nKey={`common:mcodes.${mcode}`} />
      )}
      <div className={`header-txt ${styles['full-ssn-header']}`}>
        <Trans t={t} i18nKey={'fullSSN:header'} values={{ firstName }} />
      </div>
      <div className={styles['full-ssn-body']}>
        {inputGroup(FullSSNFormNames.FULL_SSN)}
        <SimpleButton
          label={t('fullSSN:button')}
          onClickPost={onClickContinue}
          classLabel={`btn-primary`}
        />
      </div>
    </div>
  );
};

export default FullSSNPage;
